/**
 * Utilitário compartilhado para upload e extração de dados com IA.
 * Mesma lógica usada em "Processar Edital" (Upload.jsx) - retry no upload,
 * retry na extração e estratégia OCR (sem OCR primeiro, com OCR se vazio).
 *
 * Usado por: ContratoUploadIA, AtestadoForm, MatchingEditais.
 */
import { uploadFile, invokeLLM } from "@/api/apiClient";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload de arquivo com retry (até 5 tentativas por padrão).
 * @param {File} file - Arquivo a enviar
 * @param {Object} options - Opções
 * @param {number} [options.maxRetries=5] - Número máximo de tentativas
 * @param {() => boolean} [options.shouldCancel] - Função que retorna true para cancelar
 * @returns {Promise<{file_url: string}>}
 */
export async function uploadFileWithRetry(file, options = {}) {
  const { maxRetries = 5, shouldCancel } = options;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    if (shouldCancel?.()) {
      throw new Error("Processamento cancelado pelo usuário");
    }

    try {
      if (attempt > 1) {
        const waitTime = Math.min(5000 * attempt, 30000);
        await sleep(waitTime);
      }

      const result = await uploadFile(file);
      return result;
    } catch (error) {
      console.error(`[extractWithRetry] Tentativa de upload ${attempt} falhou:`, error);

      if (attempt === maxRetries) {
        throw error;
      }
    }
  }

  throw new Error("Upload falhou após todas as tentativas");
}

/**
 * Verifica se o output da IA está vazio (todos os campos-chave sem dados).
 * @param {Object} output - Objeto retornado pela IA
 * @param {string[]} keyFields - Campos que indicam se há dados extraídos
 * @returns {boolean}
 */
function isOutputEmpty(output, keyFields) {
  if (!output || typeof output !== "object") return true;
  return keyFields.every(
    (c) => output[c] == null || String(output[c]).trim() === ""
  );
}

/**
 * Verifica se algum campo crítico está vazio (para retry com OCR).
 * @param {Object} output - Objeto retornado pela IA
 * @param {string[]} criticalFields - Campos críticos que não podem estar vazios
 * @returns {boolean}
 */
function hasAnyCriticalFieldEmpty(output, criticalFields) {
  if (!output || typeof output !== "object" || criticalFields.length === 0)
    return false;
  return criticalFields.some(
    (c) => output[c] == null || String(output[c]).trim() === ""
  );
}

/**
 * Extração de dados com IA usando invokeLLM (mesmo endpoint de Processar Edital).
 * Estratégia: tenta sem OCR primeiro (PDFs digitais); se vazio, tenta com OCR.
 *
 * @param {string} fileUrl - URL do arquivo já enviado
 * @param {Object} options - Opções
 * @param {string} options.prompt - Prompt para a IA
 * @param {Object} options.schema - JSON schema para a resposta
 * @param {number} [options.maxRetries=3] - Tentativas por modo (sem OCR / com OCR)
 * @param {boolean} [options.tryOcrIfEmpty=true] - Se true, tenta com OCR quando resultado vazio
 * @param {string[]} [options.keyFieldsForEmptyCheck] - Campos para verificar se está vazio (todos)
 * @param {string[]} [options.criticalFieldsForOcrRetry] - Se QUALQUER destes estiver vazio, tenta OCR
 * @param {string} [options.document_type] - 'contrato' ou 'atestado' para fallback e ocr_page_limit
 * @param {number} [options.ocr_page_limit] - Limite de páginas para OCR (contratos: 15, atestados: 10)
 * @param {() => boolean} [options.shouldCancel] - Função que retorna true para cancelar
 * @returns {Promise<{status: string, output: Object}>}
 */
export async function extractDataWithRetry(fileUrl, options = {}) {
  const {
    prompt,
    schema,
    maxRetries = 3,
    tryOcrIfEmpty = true,
    keyFieldsForEmptyCheck = [],
    criticalFieldsForOcrRetry = [],
    document_type,
    ocr_page_limit,
    shouldCancel,
  } = options;

  const buildParams = (forceOcr) => ({
    prompt,
    file_urls: [fileUrl],
    response_json_schema: schema,
    force_ocr: forceOcr,
    ...(document_type && { document_type }),
    ...(ocr_page_limit != null && { ocr_page_limit }),
  });

  const tryExtract = async (forceOcr = false) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (shouldCancel?.()) {
        throw new Error("Processamento cancelado pelo usuário");
      }

      try {
        if (attempt > 1) {
          await sleep(5000 * attempt);
        }

        const result = await invokeLLM(buildParams(forceOcr));
        return result;
      } catch (error) {
        const status = Number(error?.status);
        const is401 = status === 401 || error?.isUnauthorized;
        console.error(
          `[extractWithRetry] Tentativa de extração ${attempt} (force_ocr=${forceOcr}) falhou:`,
          error
        );

        // 401: não retentar — sessão expirada ou token inválido; pedir login novamente
        if (is401) {
          const err = Object.assign(
            new Error("Sessão expirada ou não autenticado. Faça login novamente e tente outra vez."),
            { status: 401, isUnauthorized: true, data: error?.data }
          );
          throw err;
        }
        // 500/502/503: erro no servidor ou serviço indisponível — não insistir em várias tentativas
        if ((status === 500 || status === 502 || status === 503) && attempt >= 1) {
          const msg =
            error?.data?.message ||
            error?.message ||
            (status === 503 ? "Serviço temporariamente indisponível. Tente novamente em instantes." : "Erro no servidor. Verifique a configuração de IA e tente novamente.");
          throw Object.assign(new Error(msg), { status, data: error?.data });
        }

        const msg = String(error?.message || error?.data?.message || "").toLowerCase();
        const precisaOcr =
          msg.includes("extrair texto") ||
          msg.includes("não foi possível") ||
          msg.includes("texto do pdf");

        if (precisaOcr && !forceOcr && attempt === 1) {
          options.onOcrFallback?.();
          return tryExtract(true);
        }

        if (attempt === maxRetries) {
          throw error;
        }
      }
    }
    throw new Error("Extração falhou após todas as tentativas");
  };

  // Primeira tentativa sem OCR (melhor para PDFs digitais)
  let result = await tryExtract(false);
  let output = result?.output ?? result;

  const servidorJaUsouOcr =
    result?.text_extraction_mode === "ocr" || result?.textExtractionMode === "ocr";

  // Se vazio e tryOcrIfEmpty, tentar com OCR (PDFs escaneados) — exceto se o servidor já extraiu via OCR
  const keyFields = keyFieldsForEmptyCheck;
  const criticalFields = criticalFieldsForOcrRetry;
  const allEmpty =
    keyFields.length > 0 &&
    isOutputEmpty(output, keyFields) &&
    result?.status !== "error";
  const anyCriticalEmpty =
    criticalFields.length > 0 &&
    hasAnyCriticalFieldEmpty(output, criticalFields) &&
    result?.status !== "error";

  if (tryOcrIfEmpty && !servidorJaUsouOcr && (allEmpty || anyCriticalEmpty)) {
    options.onOcrFallback?.();
    result = await tryExtract(true);
    output = result?.output ?? result;
  }

  return result;
}

/**
 * Fluxo completo: upload + extração (mesma lógica de Processar Edital).
 * @param {File} file - Arquivo a processar
 * @param {Object} options - Opções (prompt, schema, keyFieldsForEmptyCheck, etc.)
 * @returns {Promise<{file_url: string, output: Object, result: Object}>}
 */
export async function processFileWithAI(file, options = {}) {
  const { onUploadRetry, onExtractRetry, shouldCancel, ...extractOptions } =
    options;

  const { file_url } = await uploadFileWithRetry(file, {
    maxRetries: 5,
    shouldCancel,
  });

  if (onUploadRetry) onUploadRetry(0);

  const result = await extractDataWithRetry(file_url, {
    ...extractOptions,
    shouldCancel,
  });

  const output = result?.output ?? result;
  return { file_url, output, result };
}
